// Theme editor: primary color, light/dark/system, border radius slider,
// font choice, density. Live mobile preview reflects every change.

import { useTranslation } from 'react-i18next';
import { Input, Label, Slider, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@erp/ui';
import type { Theme } from '@erp/app-builder';

interface ThemeTabProps {
  value: Theme;
  onChange: (next: Theme) => void;
}

export function ThemeTab({ value, onChange }: ThemeTabProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="space-y-1.5">
        <Label>{t('appBuilder.theme.primaryColor', { defaultValue: 'Couleur principale' })}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value.primaryColor}
            onChange={(e) => onChange({ ...value, primaryColor: e.target.value })}
            className="h-10 w-16 rounded border border-input cursor-pointer"
          />
          <Input
            value={value.primaryColor}
            onChange={(e) => onChange({ ...value, primaryColor: e.target.value })}
            className="font-mono"
            placeholder="#1B2A4A"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t('appBuilder.theme.primaryHint', { defaultValue: 'Utilisée pour les boutons, badges, onglet actif. Rétro-appliquée en mode sombre.' })}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>{t('appBuilder.theme.mode', { defaultValue: 'Apparence' })}</Label>
        <Select value={value.mode} onValueChange={(v) => onChange({ ...value, mode: v as Theme['mode'] })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="system">{t('appBuilder.theme.modeSystem', { defaultValue: 'Système' })}</SelectItem>
            <SelectItem value="light">{t('appBuilder.theme.modeLight', { defaultValue: 'Clair' })}</SelectItem>
            <SelectItem value="dark">{t('appBuilder.theme.modeDark', { defaultValue: 'Sombre' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>{t('appBuilder.theme.radius', { defaultValue: `Arrondi des coins: ${value.radius}px` })}</Label>
        <Slider
          min={0}
          max={24}
          step={1}
          value={[value.radius]}
          onValueChange={(v) => onChange({ ...value, radius: v[0] ?? 12 })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>{t('appBuilder.theme.font', { defaultValue: 'Police' })}</Label>
        <Select value={value.font} onValueChange={(v) => onChange({ ...value, font: v as Theme['font'] })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Geist">Geist (par défaut)</SelectItem>
            <SelectItem value="Inter">Inter</SelectItem>
            <SelectItem value="System">{t('appBuilder.theme.fontSystem', { defaultValue: 'Police système' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>{t('appBuilder.theme.density', { defaultValue: 'Densité' })}</Label>
        <Select value={value.density} onValueChange={(v) => onChange({ ...value, density: v as Theme['density'] })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="comfortable">{t('appBuilder.theme.densityComfortable', { defaultValue: 'Confortable' })}</SelectItem>
            <SelectItem value="compact">{t('appBuilder.theme.densityCompact', { defaultValue: 'Compacte' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
